/**
 * Owner Dashboard redesign (AB#2206) — pure view logic for the "today" Dashboard.
 *
 * Everything here derives display figures from what the API already returns
 * (Appointments list-v2 rows, the Calendar day grid, the dashboard summary).
 * It decides NO business rule: which actions a booking allows comes from the
 * backend's `allowedTransitions` / `canMarkPaid`; this file only picks which of
 * those allowed actions is the one to surface on a Dashboard row.
 */
import { allowedTargets, toLifecycleStatus, type LifecycleStatus } from '../../../lib/bookingLifecycle';
import type { SalonOwnerAppointmentRowDto } from '../../../services/appointmentsList';
import type { CalendarGridResponse } from '../../../types/api-responses';

/**
 * TEMPORARY visual rule (founder 2026-11-05): a cancellation / no-show share
 * above this percentage turns the Performance tile into a warning. Not a
 * business rule — change it here only.
 */
export const CANCELLATION_WARNING_PCT = 30;

/** Rows shown in "Today's appointments" before "+N more". */
export const TODAY_ROW_LIMIT = 8;

/** Owner browser's local calendar date "yyyy-MM-dd" (same rule as the O1 dashboard window). */
export function localYmd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local wall-clock "HH:mm". */
export function localHHmm(d: Date): string {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function hhmmToMinutes(hhmm: string | null | undefined): number | null {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

const ENDED: ReadonlySet<LifecycleStatus> = new Set<LifecycleStatus>(['Cancelled', 'NoShow']);
const UPCOMING: ReadonlySet<LifecycleStatus> = new Set<LifecycleStatus>(['Pending', 'Approved']);

export function isEnded(row: Pick<SalonOwnerAppointmentRowDto, 'status'>): boolean {
    const s = toLifecycleStatus(row.status);
    return s != null && ENDED.has(s);
}

/** The one action a Dashboard row offers. */
export type RowAction =
    | { kind: 'transition'; target: LifecycleStatus }
    | { kind: 'markPaid' }
    | null;

/** For each current status, the step that moves the visit forward. */
const FORWARD_STEP: Partial<Record<LifecycleStatus, LifecycleStatus>> = {
    Pending: 'Approved',
    Approved: 'Arrived',
    Arrived: 'Started',
    Started: 'Completed',
};

/**
 * The single next action for a row: the forward step IF the backend allows it,
 * "Mark paid" when the backend says the booking can be paid, otherwise none.
 * Decline / Cancel / No-show stay in the booking detail (they need a confirm step).
 */
export function nextRowAction(row: Pick<SalonOwnerAppointmentRowDto, 'status' | 'allowedTransitions' | 'canMarkPaid' | 'paymentStatus'>): RowAction {
    const from = toLifecycleStatus(row.status);
    const step = from ? FORWARD_STEP[from] : undefined;
    if (step && allowedTargets(row.allowedTransitions).includes(step)) {
        return { kind: 'transition', target: step };
    }
    if (row.canMarkPaid && row.paymentStatus === 'Unpaid') return { kind: 'markPaid' };
    return null;
}

export interface TodaySummary {
    /** Bookings still on the books today (cancelled / no-show excluded). */
    active: number;
    pending: number;
    ended: number;
    expected: number;
    collected: number;
    toCollect: number;
    toCollectCount: number;
}

/** Today's money and counts from today's list rows (one source → figures always agree with the list). */
export function summarizeToday(rows: readonly SalonOwnerAppointmentRowDto[]): TodaySummary {
    const out: TodaySummary = { active: 0, pending: 0, ended: 0, expected: 0, collected: 0, toCollect: 0, toCollectCount: 0 };
    for (const r of rows) {
        const status = toLifecycleStatus(r.status);
        const amount = Number(r.amount) || 0;
        if (status != null && ENDED.has(status)) {
            out.ended += 1;
        } else {
            out.active += 1;
            out.expected += amount;
            if (status === 'Pending') out.pending += 1;
        }
        // Collected = paid among bookings still on the books, so it is a share of Expected.
        if (r.paymentStatus === 'Paid' && !(status != null && ENDED.has(status))) out.collected += amount;
        if (status === 'Completed' && r.paymentStatus === 'Unpaid') {
            out.toCollect += amount;
            out.toCollectCount += 1;
        }
    }
    return out;
}

/**
 * The next booking whose customer has not arrived yet (pending or confirmed)
 * and whose slot has not ended — a booking that is due now stays "next".
 */
export function nextAppointment(
    rows: readonly SalonOwnerAppointmentRowDto[],
    nowHHmm: string,
): SalonOwnerAppointmentRowDto | null {
    const now = hhmmToMinutes(nowHHmm) ?? 0;
    const upcoming = rows
        .filter(r => {
            const s = toLifecycleStatus(r.status);
            const start = hhmmToMinutes(r.branchLocalStartTime);
            return s != null && UPCOMING.has(s) && start != null && start + (Number(r.durationMinutes) || 0) > now;
        })
        .sort((a, b) => (hhmmToMinutes(a.branchLocalStartTime) ?? 0) - (hhmmToMinutes(b.branchLocalStartTime) ?? 0));
    return upcoming[0] ?? null;
}

/** Index of the first row starting at/after now (where the "now" line goes); rows.length = after all. */
export function nowLineIndex(rows: readonly SalonOwnerAppointmentRowDto[], nowHHmm: string): number {
    const now = hhmmToMinutes(nowHHmm) ?? 0;
    const i = rows.findIndex(r => (hhmmToMinutes(r.branchLocalStartTime) ?? 0) >= now);
    return i === -1 ? rows.length : i;
}

/** A row whose slot has ended (start + duration ≤ now) or that is cancelled / no-show. */
export function isPastRow(row: Pick<SalonOwnerAppointmentRowDto, 'status' | 'branchLocalStartTime' | 'durationMinutes'>, nowHHmm: string): boolean {
    const start = hhmmToMinutes(row.branchLocalStartTime) ?? 0;
    return isEnded(row) || start + (Number(row.durationMinutes) || 0) <= (hhmmToMinutes(nowHHmm) ?? 0);
}

/** Rows in day order (time, then id). */
export function sortByStart<T extends Pick<SalonOwnerAppointmentRowDto, 'branchLocalStartTime' | 'id'>>(rows: readonly T[]): T[] {
    return [...rows].sort((a, b) =>
        ((hhmmToMinutes(a.branchLocalStartTime) ?? 0) - (hhmmToMinutes(b.branchLocalStartTime) ?? 0)) || (a.id - b.id));
}

export type ShiftState = 'working' | 'leave' | 'off';

export interface RosterEntry {
    staffId: number;
    name: string;
    photoUrl?: string | null;
    state: ShiftState;
    /** One entry per branch the person works at today. */
    shifts: { branchId: number; branchName: string; start: string; end: string; load: number }[];
}

/**
 * Who is working today, from the Calendar day grid of each branch in scope
 * (same data the Calendar shows). Load = booked minutes ÷ shift minutes (0–1),
 * counting only bookings still on the books.
 */
export function buildRoster(
    grids: readonly { branchName: string; grid: CalendarGridResponse }[],
    rows: readonly SalonOwnerAppointmentRowDto[],
): RosterEntry[] {
    const byStaff = new Map<number, RosterEntry>();
    for (const { branchName, grid } of grids) {
        const wholeDayLeave = new Set((grid.absences ?? []).filter(a => a.isWholeDay).map(a => a.staffId));
        for (const col of grid.staff ?? []) {
            const entry = byStaff.get(col.staffId)
                ?? { staffId: col.staffId, name: col.displayName, photoUrl: col.photoUrl, state: 'off' as ShiftState, shifts: [] };
            const start = hhmmToMinutes(col.shiftStart);
            const end = hhmmToMinutes(col.shiftEnd);
            if (!grid.isClosed && !wholeDayLeave.has(col.staffId) && start != null && end != null && end > start) {
                const booked = rows
                    .filter(r => r.staffId === col.staffId && r.branchId === grid.branchId && !isEnded(r))
                    .reduce((sum, r) => sum + (Number(r.durationMinutes) || 0), 0);
                entry.shifts.push({
                    branchId: grid.branchId,
                    branchName,
                    start: col.shiftStart as string,
                    end: col.shiftEnd as string,
                    load: Math.min(1, booked / (end - start)),
                });
                entry.state = 'working';
            } else if (entry.state !== 'working' && wholeDayLeave.has(col.staffId)) {
                entry.state = 'leave';
            }
            byStaff.set(col.staffId, entry);
        }
    }
    const rank: Record<ShiftState, number> = { working: 0, leave: 1, off: 2 };
    return Array.from(byStaff.values()).sort((a: RosterEntry, b: RosterEntry) =>
        (rank[a.state] - rank[b.state])
        || ((hhmmToMinutes(a.shifts[0]?.start) ?? 0) - (hhmmToMinutes(b.shifts[0]?.start) ?? 0))
        || a.name.localeCompare(b.name));
}

export interface OutcomeShares {
    completed: number;
    confirmed: number;
    pending: number;
    cancelled: number;
}

/**
 * Percentages from the dashboard summary's `appointmentStats` (the backend sends
 * percentages labelled Pending / Confirm / Rejected / Completed; "Rejected" =
 * Cancelled + NoShow in GetDashboardSummaryQueryHandler). Read by label, never by position.
 */
export function outcomeShares(labels: readonly string[], values: readonly number[]): OutcomeShares {
    const at = (name: string) => {
        const i = labels.findIndex(l => l.toLowerCase() === name);
        return i >= 0 ? Number(values[i]) || 0 : 0;
    };
    return { completed: at('completed'), confirmed: at('confirm'), pending: at('pending'), cancelled: at('rejected') };
}

export function isCancellationWarning(pct: number): boolean {
    return pct > CANCELLATION_WARNING_PCT;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * The summary's daily labels are invariant "MMM dd" ("Nov 04"). Shown as a
 * short local date ("4 Nov" / Arabic). Anything unexpected is returned as-is.
 */
export function dailyLabel(label: string, year: number, language: string): string {
    const m = /^([A-Za-z]{3})\s+(\d{1,2})$/.exec((label ?? '').trim());
    if (!m) return label;
    const month = MONTHS.indexOf(m[1].toLowerCase());
    if (month < 0) return label;
    const d = new Date(Date.UTC(year, month, Number(m[2])));
    return d.toLocaleDateString(language === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** A "nice" axis maximum ≥ value (1, 2, 5 × 10ⁿ), never 0. */
export function niceMax(value: number): number {
    if (!(value > 0)) return 1;
    const exp = Math.floor(Math.log10(value));
    const base = 10 ** exp;
    for (const f of [1, 2, 5, 10]) {
        if (value <= f * base) return f * base;
    }
    return 10 * base;
}
